// Gradient Map — maps the luminance of each pixel to a color from a gradient.
// Dark pixels get the start color, light pixels get the end color, and
// intermediate luminances are interpolated along the gradient.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface GradientStop {
  position: number; // 0..1
  color: { r: number; g: number; b: number }; // 0..255
}

export interface GradientMapParams {
  /**
   * Gradient stops. At least 2 stops are needed (start + end). Photoshop
   * supports arbitrary multi-stop gradients; we interpolate linearly between
   * adjacent stops by position.
   */
  stops: GradientStop[];
  /**
   * When true, the gradient is reversed (light pixels get the start color).
   */
  reverse: boolean;
  /**
   * Dithering reduces banding by adding noise. Default true.
   */
  dither: boolean;
}

const DEFAULT_STOPS: GradientStop[] = [
  { position: 0, color: { r: 0, g: 0, b: 0 } },
  { position: 1, color: { r: 255, g: 255, b: 255 } },
];

export const GradientMap: Filter<GradientMapParams> = {
  id: "gradient-map",
  displayName: "Gradient Map",
  defaultParams: { stops: DEFAULT_STOPS, reverse: false, dither: true },

  apply(input: PixelBuffer, params: GradientMapParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const stops = params.stops.length >= 2 ? params.stops : DEFAULT_STOPS;
    const sorted = [...stops].sort((a, b) => a.position - b.position);
    const rev = params.reverse;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0; dst[o + 1] = 0; dst[o + 2] = 0; dst[o + 3] = 0;
        continue;
      }

      // Luminance in 0..1
      let t = (r * 77 + g * 150 + b * 29) / (255 * 256); // Rec.601
      t = clamp01(t);
      if (rev) t = 1 - t;

      // Find the two surrounding stops and interpolate.
      const col = sampleGradient(sorted, t);

      // Optional dither: add noise proportional to step size
      if (params.dither) {
        const noise = (Math.random() - 0.5) * (255 / sorted.length);
        dst[o] = clampByte(col.r + noise);
        dst[o + 1] = clampByte(col.g + noise);
        dst[o + 2] = clampByte(col.b + noise);
      } else {
        dst[o] = col.r;
        dst[o + 1] = col.g;
        dst[o + 2] = col.b;
      }
      dst[o + 3] = a;
    }
    return out;
  },
};

function sampleGradient(stops: GradientStop[], t: number): { r: number; g: number; b: number } {
  if (t <= stops[0]!.position) return stops[0]!.color;
  if (t >= stops[stops.length - 1]!.position) return stops[stops.length - 1]!.color;

  for (let i = 0; i < stops.length - 1; i++) {
    const lo = stops[i]!;
    const hi = stops[i + 1]!;
    if (t >= lo.position && t <= hi.position) {
      const span = hi.position - lo.position;
      const f = span === 0 ? 0 : (t - lo.position) / span;
      return {
        r: Math.round(lo.color.r + (hi.color.r - lo.color.r) * f),
        g: Math.round(lo.color.g + (hi.color.g - lo.color.g) * f),
        b: Math.round(lo.color.b + (hi.color.b - lo.color.b) * f),
      };
    }
  }
  return stops[stops.length - 1]!.color;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}
